import {
  avatarForGender,
  validateAvatarConfig,
  type AvatarConfig,
  type AvatarGender,
} from "@guandan/avatar-core";

export interface AvatarPortrait {
  readonly id: string;
  readonly gender: AvatarGender;
  readonly label: string;
  readonly ageLabel: string;
  readonly imageUrl: string;
  readonly config: AvatarConfig;
}

function portrait(
  id: string,
  gender: AvatarGender,
  label: string,
  ageLabel: string,
  imageUrl: string,
  changes: Partial<AvatarConfig>,
): AvatarPortrait {
  return Object.freeze({
    id,
    gender,
    label,
    ageLabel,
    imageUrl,
    config: validateAvatarConfig({ ...avatarForGender(gender), ...changes }),
  });
}

export const AVATAR_PORTRAITS: readonly AvatarPortrait[] = Object.freeze([
  portrait(
    "w-girl-1",
    "woman",
    "元气女孩",
    "女孩",
    "/avatars/portrait-w-girl-1.jpg",
    {
      face: "face-w-bright",
      hair: "hair-w-bun",
      outfit: "outfit-w-cream",
      accessory: "accessory-w-pearl",
    },
  ),
  portrait(
    "w-girl-2",
    "woman",
    "明眸少女",
    "少女",
    "/avatars/portrait-w-girl-2.jpg",
    {
      face: "face-w-soft",
      hair: "hair-w-layer",
      outfit: "outfit-w-jade",
      accessory: "accessory-w-pearl",
    },
  ),
  portrait(
    "w-young-1",
    "woman",
    "清雅盘发",
    "青年",
    "/avatars/portrait-w-young-1.jpg",
    {
      face: "face-w-soft",
      hair: "hair-w-wave",
      outfit: "outfit-w-cream",
      accessory: "accessory-w-pearl",
    },
  ),
  portrait(
    "w-young-2",
    "woman",
    "利落短发",
    "青年",
    "/avatars/portrait-w-young-2.jpg",
    {
      face: "face-w-clear",
      hair: "hair-w-bob",
      outfit: "outfit-w-jade",
      accessory: "accessory-w-watch",
    },
  ),
  portrait(
    "w-mature-1",
    "woman",
    "从容长卷",
    "中年",
    "/avatars/portrait-w-mature-1.jpg",
    {
      face: "face-w-defined",
      hair: "hair-w-tail",
      outfit: "outfit-w-jade",
      accessory: "accessory-w-pearl",
    },
  ),
  portrait(
    "w-mature-2",
    "woman",
    "知性短卷",
    "中年",
    "/avatars/portrait-w-mature-2.jpg",
    {
      face: "face-w-bright",
      hair: "hair-w-shoulder",
      outfit: "outfit-w-cream",
      accessory: "accessory-w-watch",
    },
  ),
  portrait(
    "m-boy-1",
    "man",
    "清新男孩",
    "男孩",
    "/avatars/portrait-m-boy-1.jpg",
    {
      face: "face-m-soft",
      hair: "hair-m-swept",
      outfit: "outfit-m-navy",
      accessory: "accessory-m-none",
    },
  ),
  portrait(
    "m-boy-2",
    "man",
    "俊朗少男",
    "少男",
    "/avatars/portrait-m-boy-2.jpg",
    {
      face: "face-m-clear",
      hair: "hair-m-layer",
      outfit: "outfit-m-grey",
      accessory: "accessory-m-none",
    },
  ),
  portrait(
    "m-young-1",
    "man",
    "清朗侧分",
    "青年",
    "/avatars/portrait-m-young-1.jpg",
    {
      face: "face-m-clear",
      hair: "hair-m-texture",
      outfit: "outfit-m-navy",
      accessory: "accessory-m-watch",
    },
  ),
  portrait(
    "m-young-2",
    "man",
    "自然碎发",
    "青年",
    "/avatars/portrait-m-young-2.jpg",
    {
      face: "face-m-soft",
      hair: "hair-m-side",
      outfit: "outfit-m-navy",
      accessory: "accessory-m-none",
    },
  ),
  portrait(
    "m-mature-1",
    "man",
    "沉稳背梳",
    "中年",
    "/avatars/portrait-m-mature-1.jpg",
    {
      face: "face-m-defined",
      hair: "hair-m-crop",
      outfit: "outfit-m-navy",
      accessory: "accessory-m-watch",
    },
  ),
  portrait(
    "m-mature-2",
    "man",
    "儒雅眼镜",
    "中年",
    "/avatars/portrait-m-mature-2.jpg",
    {
      face: "face-m-clear",
      hair: "hair-m-wave",
      outfit: "outfit-m-grey",
      accessory: "accessory-m-glasses",
    },
  ),
]);

const FALLBACK_HAIR: Readonly<
  Record<AvatarGender, Readonly<Record<string, string>>>
> = {
  woman: {
    "hair-w-wave": "w-young-1",
    "hair-w-bob": "w-young-2",
    "hair-w-tail": "w-mature-1",
    "hair-w-shoulder": "w-mature-2",
    "hair-w-bun": "w-girl-1",
    "hair-w-layer": "w-girl-2",
    "hair-w-pixie": "w-young-2",
    "hair-w-curl": "w-mature-1",
  },
  man: {
    "hair-m-texture": "m-young-1",
    "hair-m-side": "m-young-2",
    "hair-m-crop": "m-mature-1",
    "hair-m-wave": "m-mature-2",
    "hair-m-swept": "m-boy-1",
    "hair-m-layer": "m-boy-2",
    "hair-m-sport": "m-young-2",
    "hair-m-casual": "m-young-1",
  },
};

export function avatarPortraitsForGender(
  gender: AvatarGender,
): readonly AvatarPortrait[] {
  return AVATAR_PORTRAITS.filter((item) => item.gender === gender);
}

export function avatarPortraitForConfig(config: AvatarConfig): AvatarPortrait {
  const id = FALLBACK_HAIR[config.gender][config.hair];
  const matched =
    AVATAR_PORTRAITS.find((item) => item.id === id) ??
    AVATAR_PORTRAITS.find((item) => item.gender === config.gender) ??
    AVATAR_PORTRAITS[0];
  if (!matched) throw new Error("头像目录为空");
  return matched;
}
